import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';

// Map of invitationId → dedicated printable page path
const INVITATION_PRINT_LINKS = {
  'birthday-jenine-charisse': '/debut/jenine-charisse/printable',
  'sofia-alexandra-debut': '/invitations/custom/sofia-alexandra-debut/printable',
};

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const SOCKET_URL = API_BASE_URL || `${window.location.protocol}//${window.location.hostname}:5011`;

const CODE_STORAGE_KEY = 'seat_checker_access_code';
const ADMIN_CHECKER_DEEPLINK_KEY = 'seat_checker_admin_deeplink';
const MERIZHELLE_INVITATION_ID = 'merizhelle-kate-c-escondo-marvic-m-aranza-wedding';
const FRANCES_INVITATION_ID = 'frances-maurice-gwyneth-b-maglalang-debut';

function syncAccessCodeUrl(code) {
  if (typeof window === 'undefined') return;

  const normalizedCode = String(code || '').trim().toUpperCase();
  const nextUrl = normalizedCode
    ? `${window.location.pathname}?code=${encodeURIComponent(normalizedCode)}`
    : window.location.pathname;

  window.history.replaceState({}, '', nextUrl);
}

function consumeAdminDeepLink(code) {
  const normalizedCode = String(code || '').trim().toUpperCase();
  const raw = localStorage.getItem(ADMIN_CHECKER_DEEPLINK_KEY) || '';

  if (!raw || !normalizedCode) return false;

  try {
    const parsed = JSON.parse(raw);
    const savedCode = String(parsed?.code || '').trim().toUpperCase();
    const expiresAt = Number(parsed?.expiresAt || 0);
    const isValid = savedCode === normalizedCode && Number.isFinite(expiresAt) && expiresAt > Date.now();
    localStorage.removeItem(ADMIN_CHECKER_DEEPLINK_KEY);
    return isValid;
  } catch {
    localStorage.removeItem(ADMIN_CHECKER_DEEPLINK_KEY);
    return false;
  }
}

export default function SeatChecker() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [accessCodeInput, setAccessCodeInput] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [joinedInvitationId, setJoinedInvitationId] = useState('');
  const [showNoPrintableModal, setShowNoPrintableModal] = useState(false);
  const navigate = useNavigate();

  const urlCode = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return String(new URLSearchParams(window.location.search).get('code') || '').trim().toUpperCase();
  }, []);

  const isMerizhelleChecker = data?.invitationId === MERIZHELLE_INVITATION_ID || joinedInvitationId === MERIZHELLE_INVITATION_ID;
  const isFrancesChecker = data?.invitationId === FRANCES_INVITATION_ID || joinedInvitationId === FRANCES_INVITATION_ID;
  const hasReservationDetails = isMerizhelleChecker || isFrancesChecker;
  const hasChildReservationDetails = isMerizhelleChecker;
  const hasEmailDetails = isFrancesChecker;
  const entries = Array.isArray(data?.entries) ? data.entries : [];
  const rawChildSeatsUsed = data?.childSeatsUsed ?? entries.reduce((sum, entry) => sum + (entry.attending === 'yes' ? Number(entry.childSeats || 0) : 0), 0);
  const childSeatsUsed = hasChildReservationDetails ? rawChildSeatsUsed : 0;
  const adultSeatsUsed = hasChildReservationDetails ? Math.max(0, Number(data?.seatsUsed || 0) - childSeatsUsed) : Number(data?.seatsUsed || 0);
  const reservationTableMinWidth = hasReservationDetails ? (hasChildReservationDetails ? '860px' : hasEmailDetails ? '900px' : '800px') : '760px';

  useEffect(() => {
    if (urlCode) {
      if (consumeAdminDeepLink(urlCode)) {
        setAccessCode(urlCode);
        setAccessCodeInput(urlCode);
        localStorage.setItem(CODE_STORAGE_KEY, urlCode);
        return;
      }

      syncAccessCodeUrl('');
    }

    const saved = localStorage.getItem(CODE_STORAGE_KEY) || '';
    setAccessCode(saved);
    setAccessCodeInput(saved);
  }, [urlCode]);

  const endpoint = useMemo(() => {
    if (!accessCode) return null;
    const encodedCode = encodeURIComponent(accessCode);
    return `${API_BASE_URL}/rsvp/public-summary?code=${encodedCode}`;
  }, [accessCode]);

  const loadSummary = useCallback(async ({ silent = false } = {}) => {
    if (!endpoint) return;

    try {
      if (!silent) setLoading(true);
      setError('');
      const res = await fetch(endpoint);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Failed to load seat summary');
      setData(json);
      setJoinedInvitationId(json.invitationId || '');
    } catch (err) {
      setError(err.message || 'Failed to load seat summary');
      setJoinedInvitationId('');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    if (!accessCode) {
      setLoading(false);
      setData(null);
      setJoinedInvitationId('');
      return;
    }

    loadSummary();
  }, [loadSummary, accessCode]);

  useEffect(() => {
    if (!accessCode) return;

    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling']
    });

    socket.on('connect', () => {
      socket.emit('seat-checker:join', { code: accessCode });
    });

    socket.on('seat-checker:joined', (payload) => {
      if (payload?.invitationId) {
        setJoinedInvitationId(payload.invitationId);
      }
    });

    socket.on('rsvp:updated', (payload) => {
      if (!payload?.invitationId || payload.invitationId === joinedInvitationId) {
        loadSummary({ silent: true });
      }
    });

    socket.on('seat-checker:denied', () => {
      setError('Access code is invalid.');
    });

    return () => {
      if (joinedInvitationId) socket.emit('seat-checker:leave', joinedInvitationId);
      socket.disconnect();
    };
  }, [loadSummary, accessCode, joinedInvitationId]);

  function applyAccessCode() {
    const normalized = accessCodeInput.trim().toUpperCase();
    if (!normalized) {
      setError('Please enter seat checker access code.');
      return;
    }

    setError('');
    setAccessCode(normalized);
    localStorage.setItem(CODE_STORAGE_KEY, normalized);
    syncAccessCodeUrl('');
  }

  function clearAccessCode() {
    setAccessCode('');
    setAccessCodeInput('');
    setData(null);
    setJoinedInvitationId('');
    localStorage.removeItem(CODE_STORAGE_KEY);
    localStorage.removeItem(ADMIN_CHECKER_DEEPLINK_KEY);
    syncAccessCodeUrl('');
  }

  return (
    <main style={{ minHeight: '100vh', background: '#f6f1e8', padding: '32px 16px', fontFamily: 'Georgia, serif' }}>
      <section style={{ maxWidth: hasReservationDetails ? '920px' : '720px', margin: '0 auto', background: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
        <h1 style={{ margin: 0, color: '#3a2c22' }}>
          {isMerizhelleChecker ? 'Merizhelle & Marvic Seat Checker' : isFrancesChecker ? 'Frances Seat Checker' : 'Seat & Attendee Checker'}
        </h1>
        <p style={{ marginTop: '8px', color: '#6b5a4c' }}>
          Invitation ID: <strong>{data?.invitationId || joinedInvitationId || '-'}</strong>
        </p>
        {hasReservationDetails && (
          <p style={{ margin: '4px 0 0', color: '#8a5b2e', fontSize: '14px', lineHeight: 1.6 }}>
            {isFrancesChecker
              ? 'RSVP summary includes guest email, relationship group, and 1 adult reserved seat per RSVP. Duplicate email submissions are blocked.'
              : 'RSVP summary includes reserved seats, children 12 years old and below, and guest relationship group.'}
          </p>
        )}

        {!accessCode && (
          <div style={{ marginTop: '18px', background: '#f9f5ee', border: '1px solid #eadfcd', borderRadius: '12px', padding: '14px' }}>
            <p style={{ margin: '0 0 10px', color: '#6b5a4c' }}>Enter seat checker access code to continue.</p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <input
                type="text"
                value={accessCodeInput}
                onChange={(e) => setAccessCodeInput(e.target.value.toUpperCase())}
                placeholder="Access code"
                autoComplete="off"
                style={{ flex: '1 1 220px', border: '1px solid #d4c8b4', borderRadius: '10px', padding: '10px 12px', fontSize: '14px', textTransform: 'uppercase' }}
              />
              <button type="button" onClick={applyAccessCode} style={{ border: 'none', borderRadius: '10px', background: '#7c2d12', color: '#fff', padding: '10px 14px', cursor: 'pointer' }}>
                Login
              </button>
            </div>
          </div>
        )}

        {accessCode && (
          <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <p style={{ margin: 0, color: '#6b5a4c', fontSize: '13px' }}>Access code active: <strong>{accessCode}</strong></p>
            <button type="button" onClick={clearAccessCode} style={{ border: '1px solid #d4c8b4', borderRadius: '10px', background: '#fff', color: '#3a2c22', padding: '7px 10px', cursor: 'pointer' }}>
              Use different code
            </button>
          </div>
        )}

        {loading && <p style={{ color: '#6b5a4c' }}>Loading summary...</p>}
        {!loading && error && <p style={{ color: '#b42318' }}>{error}</p>}

        {!loading && !error && data && (
          <>
            {hasReservationDetails ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginTop: '16px' }}>
                <StatCard label="Total Responses" value={data.totalRsvps} />
                <StatCard label={isFrancesChecker ? 'Reserved Seat RSVPs' : 'Reserved Seats'} value={data.seatsUsed} />
                {hasChildReservationDetails ? (
                  <>
                    <StatCard label="Adult Seats" value={adultSeatsUsed} />
                    <StatCard label="Children 12 & Below" value={childSeatsUsed} />
                  </>
                ) : (
                  <StatCard label="Attending Entries" value={data.attendingCount} />
                )}
                <StatCard label="Declines" value={data.decliningCount} />
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginTop: '16px' }}>
                <StatCard label="Total RSVPs" value={data.totalRsvps} />
                <StatCard label="Confirmed Guests" value={data.totalGuests} />
                <StatCard label="Attending Entries" value={data.attendingCount} />
                <StatCard label="Declining Entries" value={data.decliningCount} />
                <StatCard label="Seats Used" value={data.seatsUsed} />
              </div>
            )}

            <div style={{ marginTop: '20px', padding: '16px', background: '#f9f5ee', border: '1px solid #eadfcd', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <div>
                <p style={{ margin: 0, fontWeight: 700, color: '#3a2c22', fontSize: '15px' }}>Printable Invitation</p>
                <p style={{ margin: '4px 0 0', color: '#6b5a4c', fontSize: '13px' }}>View and download the printable invitation as a PDF.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  const link = INVITATION_PRINT_LINKS[data.invitationId];
                  if (link) navigate(link);
                  else setShowNoPrintableModal(true);
                }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  background: '#7c2d12', color: '#fff', border: 'none',
                  borderRadius: '10px', padding: '10px 16px', fontSize: '14px',
                  fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap'
                }}
              >
                🖨️ Go To Printable Copy
              </button>
            </div>

            <div style={{ marginTop: '24px' }}>
              <h2 style={{ margin: 0, color: '#3a2c22', fontSize: '24px' }}>RSVP List</h2>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#9a8b7e', fontStyle: 'italic' }}>Click a row to see details</p>
              {entries.length > 0 ? (
                <div style={{ marginTop: '12px', overflowX: 'auto', border: '1px solid #eadfcd', borderRadius: '12px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: reservationTableMinWidth }}>
                    <thead>
                      <tr style={{ background: '#f4ebde', color: '#5c4b3f' }}>
                        <th style={thStyle}>#</th>
                        <th style={thStyle}>Name</th>
                        {hasEmailDetails && <th style={thStyle}>Email</th>}
                        <th style={thStyle}>RSVP</th>
                        {hasReservationDetails && <th style={thStyle}>Relationship</th>}
                        <th style={thStyle}>{isFrancesChecker ? 'Reserved Seat' : hasReservationDetails ? 'Reserved Seats' : 'Seats'}</th>
                        {hasChildReservationDetails && <th style={thStyle}>Children</th>}
                        <th style={thStyle}>Message</th>
                        <th style={thStyle}>Submitted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry, idx) => (
                        <tr
                          key={entry.id || idx}
                          onClick={() => setSelected({ ...entry, idx })}
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#fdf7ef'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}
                        >
                          <td style={tdStyle}>{idx + 1}</td>
                          <td style={{ ...tdStyle, maxWidth: 'clamp(80px, 50vw, 200px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.name || '-'}</td>
                          {hasEmailDetails && <td style={{ ...tdStyle, maxWidth: 'clamp(120px, 45vw, 220px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.email || '-'}</td>}
                          <td style={{ ...tdStyle, textAlign: 'center', fontSize: '18px' }}>{entry.attending === 'yes' ? '✓' : '✕'}</td>
                          {hasReservationDetails && <td style={tdStyle}>{entry.relationshipLabel || '-'}</td>}
                          <td style={tdStyle}>{entry.seats ?? 0}</td>
                          {hasChildReservationDetails && <td style={tdStyle}>{entry.attending === 'yes' ? Number(entry.childSeats || 0) : '-'}</td>}
                          <td style={{ ...tdStyle, maxWidth: 'clamp(80px, 50vw, 180px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.message || '-'}</td>
                          <td style={tdStyle}>{entry.submittedAt || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ marginTop: '12px', color: '#6b5a4c' }}>No RSVP entries yet.</p>
              )}
            </div>
          </>
        )}
      </section>

      {selected && <EntryModal entry={selected} showChildSeats={hasChildReservationDetails} showEmail={hasEmailDetails} onClose={() => setSelected(null)} />}
      {showNoPrintableModal && <NoPrintableModal onClose={() => setShowNoPrintableModal(false)} />}
    </main>
  );
}

const thStyle = {
  textAlign: 'left',
  fontSize: 'clamp(10px, 2.6vw, 13px)',
  fontWeight: 700,
  letterSpacing: '0.4px',
  padding: 'clamp(7px, 1.8vw, 10px) clamp(8px, 2.2vw, 12px)',
  borderBottom: '1px solid #e1d5c2'
};

const tdStyle = {
  fontSize: 'clamp(11px, 2.8vw, 14px)',
  padding: 'clamp(7px, 1.8vw, 10px) clamp(8px, 2.2vw, 12px)',
  color: '#3a2c22',
  borderBottom: '1px solid #f0e7da',
  verticalAlign: 'top'
};

function StatCard({ label, value }) {
  return (
    <div style={{ background: '#f9f5ee', border: '1px solid #eadfcd', borderRadius: '12px', padding: '14px' }}>
      <p style={{ margin: 0, fontSize: '12px', letterSpacing: '0.5px', color: '#7a6b5e', textTransform: 'uppercase' }}>{label}</p>
      <p style={{ margin: '8px 0 0', fontSize: '28px', color: '#3a2c22', fontWeight: 700 }}>{value}</p>
    </div>
  );
}

function EntryModal({ entry, showChildSeats = false, showEmail = false, onClose }) {
  // Close on backdrop click
  function handleBackdrop(e) {
    if (e.target === e.currentTarget) onClose();
  }

  // Close on Escape key
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isAttending = entry.attending === 'yes';
  const hasRelationship = Boolean(entry.relationshipLabel);
  const hasChildSeats = showChildSeats && isAttending && entry.childSeats !== undefined;

  return (
    <div
      onClick={handleBackdrop}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '16px'
      }}
    >
      <div style={{
        background: '#fff', borderRadius: '16px', padding: '28px 24px',
        width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        fontFamily: 'Georgia, serif', position: 'relative'
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: '14px', right: '16px',
            background: 'none', border: 'none', fontSize: '20px',
            cursor: 'pointer', color: '#7a6b5e', lineHeight: 1
          }}
          aria-label="Close"
        >✕</button>

        <p style={{ margin: '0 0 16px', fontSize: '11px', letterSpacing: '0.5px', color: '#9a8b7e', textTransform: 'uppercase' }}>
          Entry #{entry.idx + 1}
        </p>
        <h2 style={{ margin: '0 0 4px', fontSize: '22px', color: '#3a2c22' }}>{entry.name || '—'}</h2>
        <span style={{
          display: 'inline-block', marginBottom: '20px',
          padding: '3px 12px', borderRadius: '999px', fontSize: '13px', fontWeight: 700,
          background: isAttending ? '#d4edda' : '#f8d7da',
          color: isAttending ? '#1a6630' : '#842029'
        }}>
          {isAttending ? '✓ Attending' : '✕ Not Attending'}
        </span>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <Detail label={showEmail ? 'Reserved Seat' : 'Reserved Seats'} value={entry.seats ?? 0} />
          {showEmail && <Detail label="Email" value={entry.email || '—'} />}
          {hasRelationship && <Detail label="Relationship" value={entry.relationshipLabel} />}
          {hasChildSeats && <Detail label="Children" value={Number(entry.childSeats || 0)} />}
          <Detail label="Submitted" value={entry.submittedAt || '—'} />
        </div>

        {entry.message && (
          <div style={{ background: '#f9f5ee', border: '1px solid #eadfcd', borderRadius: '10px', padding: '12px' }}>
            <p style={{ margin: '0 0 4px', fontSize: '11px', letterSpacing: '0.5px', color: '#9a8b7e', textTransform: 'uppercase' }}>Message</p>
            <p style={{ margin: 0, fontSize: '14px', color: '#3a2c22', lineHeight: 1.6 }}>{entry.message}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p style={{ margin: '0 0 2px', fontSize: '11px', letterSpacing: '0.5px', color: '#9a8b7e', textTransform: 'uppercase' }}>{label}</p>
      <p style={{ margin: 0, fontSize: '15px', color: '#3a2c22', fontWeight: 600 }}>{value}</p>
    </div>
  );
}

function NoPrintableModal({ onClose }) {
  function handleBackdrop(e) {
    if (e.target === e.currentTarget) onClose();
  }

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={handleBackdrop}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '16px'
      }}
    >
      <div style={{
        background: '#fff', borderRadius: '16px', padding: '28px 24px',
        width: '100%', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        fontFamily: 'Georgia, serif', position: 'relative', textAlign: 'center'
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: '14px', right: '16px',
            background: 'none', border: 'none', fontSize: '20px',
            cursor: 'pointer', color: '#7a6b5e', lineHeight: 1
          }}
          aria-label="Close"
        >✕</button>

        <div style={{ fontSize: '40px', marginBottom: '12px' }}>🖨️</div>
        <h2 style={{ margin: '0 0 8px', fontSize: '20px', color: '#3a2c22' }}>Printable Not Available</h2>
        <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#6b5a4c', lineHeight: 1.6 }}>
          A printable version is not yet set up for this invitation. Please contact our customer service to request or enable this feature.
        </p>
        <a
          href="mailto:logicatechnology.dev@gmail.com?subject=Printable Invitation Request"
          style={{
            display: 'inline-block', background: '#7c2d12', color: '#fff',
            borderRadius: '10px', padding: '10px 20px', fontSize: '14px',
            fontWeight: 600, textDecoration: 'none'
          }}
        >
          Contact Customer Service
        </a>
        <button
          onClick={onClose}
          style={{
            display: 'block', margin: '12px auto 0', background: 'none',
            border: '1px solid #d4c8b4', borderRadius: '10px', padding: '8px 20px',
            fontSize: '13px', color: '#6b5a4c', cursor: 'pointer'
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}
